<template>
  <div
    class="alert-message"
    :class="[effectiveType, size]"
  >
    <FlexLayout :align="isSmallScreen ? 'start' : 'center'">
      <FlexItem grow>
        <FlexLayout
          :align="isSmallScreen ? undefined : 'center'"
          :direction="isSmallScreen ? 'column' : 'row'"
        >
          <FlexItem>
            <FancyIcon
              :align="isSmallScreen ? 'center' : undefined"
              :color="iconColor"
              :icon="iconId"
            />
          </FlexItem>
          <FlexItem grow>
            <FlexLayout
              align="start"
              direction="column"
              gap="xs"
            >
              <span
                v-if="title"
                class="title"
              >
                {{ title }}
              </span>
              <span class="message">{{ message }}</span>
              <slot />
            </FlexLayout>
          </FlexItem>
        </FlexLayout>
      </FlexItem>
      <FlexItem v-if="dismissible">
        <BaseButton
          :color="effectiveType"
          rounded
          size="lg"
          square
          variant="light"
          @click="$emit('dismiss')"
        >
          <BaseIcon id="x-circle" />
        </BaseButton>
      </FlexItem>
    </FlexLayout>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { useBreakpoints } from '../../composables/useBreakpoints';

const { isSmallScreen } = useBreakpoints();

import BaseButton from '../atoms/BaseButton.vue';
import BaseIcon from '../atoms/BaseIcon.vue';
import FlexItem from '../layouts/flex/FlexItem.vue';
import FlexLayout from '../layouts/flex/FlexLayout.vue';
import FancyIcon from './FancyIcon.vue';

export interface AlertMessageProps {
  type?: 'error' | 'success' | 'warning' | 'info' | 'primary' | 'secondary';
  title?: string;
  message: string;
  size?: 'sm' | null;
  dismissible?: boolean;
  /**
   * Custom icon to display instead of type-based icon
   * Overrides the default icon determined by the type prop
   * @default undefined (uses type-based icon)
   * @example
   * ```vue
   * <AlertMessage type="info" icon="airplane" message="Flight departing soon" />
   * ```
   */
  icon?: string;
}

const props = withDefaults(defineProps<AlertMessageProps>(), {
  type: 'info',
  size: null,
  dismissible: false,
});

const effectiveType = computed(() => props.type || 'error');

const iconId = computed(() => {
  // Use custom icon if provided, otherwise determine by type
  if (props.icon) {
    return props.icon;
  }

  switch (effectiveType.value) {
    case 'success':
      return 'check-circle';
    case 'warning':
      return 'warning-circle';
    case 'info':
      return 'info';
    case 'error':
      return 'warning';
    default:
      return 'circle';
  }
});

const iconColor = computed(() => effectiveType.value);

defineEmits<{
  (e: 'dismiss'): void;
}>();
</script>

<style>
.alert-message {
  border-radius: var(--border-radius-md);
  padding: var(--gap-sm);
  display: flex;
  align-items: center;
  gap: var(--gap-sm);
  border: var(--size-border-thin) solid var(--alert-border);
  background: var(--color-light);
  position: relative;
  min-width: 100%;
  color: var(--color-neutral-600);

  .title {
    font-weight: var(--font-semi);
  }

  .message {
    font-weight: var(--font-normal);
  }

  &:not(:last-child) {
    margin-bottom: var(--gap-sm);
  }

  &.error {
    --alert-border: var(--color-error-300);
  }

  &.success {
    --alert-border: var(--color-success-300);
  }

  &.warning {
    --alert-border: var(--color-warning-300);
  }

  &.info {
    --alert-border: var(--color-info-300);
  }

  &.primary {
    --alert-border: var(--color-primary-300);
  }

  &.secondary {
    --alert-border: var(--color-secondary-300);
  }
}
</style>
